(() => {
  const FALLBACK_URL = '/pages/contact#treedix-live-chat';
  const root = document.querySelector('[data-treedix-live-chat]');
  const fallbackUrl = (root && root.dataset.fallbackUrl) || FALLBACK_URL;

  if (root && root.dataset.ready === 'true') return;
  if (root) root.dataset.ready = 'true';

  const state = {
    opening: false,
    lastLauncher: null
  };

  const NATIVE_LAUNCHER_SELECTORS = [
    '[data-shopify-inbox-launcher]',
    '[data-shopify-chat-launcher]',
    '[data-shopify-inbox]',
    '[data-shopify-chat]',
    '[id*="shopify-chat" i]',
    '[id*="shopify-inbox" i]',
    '[class*="shopify-chat" i]',
    '[class*="shopify-inbox" i]',
    'button[aria-label="Chat" i]',
    'button[aria-label*="chat" i]',
    '[role="button"][aria-label="Chat" i]',
    '[role="button"][aria-label*="chat" i]',
    'iframe[title="Chat" i]',
    'iframe[title*="chat" i]',
    'iframe[title*="shopify inbox" i]',
    'iframe[id*="shopify-chat" i]',
    'iframe[id*="shopify-inbox" i]',
    'iframe[class*="shopify-chat" i]',
    'iframe[class*="shopify-inbox" i]',
    'iframe[src*="shopify-chat" i]',
    'iframe[src*="shopify-inbox" i]',
    'iframe[src*="inbox.shopify" i]',
    'iframe[src*="chat.shopify" i]'
  ];

  const REJECT_SELECTORS = [
    '[data-treedix-live-chat]',
    '[data-treedix-live-chat-trigger]',
    '[aria-label*="search" i]',
    '[aria-label*="cart" i]',
    '[aria-label*="account" i]',
    '[aria-label*="translate" i]',
    '[class*="search" i]',
    '[class*="cart" i]',
    '[class*="account" i]',
    '[class*="translate" i]'
  ];

  const isVisible = (node) => {
    if (!node || node.hidden) return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    return node.getClientRects().length > 0 || node.offsetParent !== null;
  };

  const isRejected = (node) => {
    if (!node || !node.matches) return true;
    if (root && root.contains(node)) return true;

    return REJECT_SELECTORS.some((selector) => {
      try {
        return node.matches(selector) || Boolean(node.closest(selector));
      } catch (error) {
        return false;
      }
    });
  };

  const hasChatText = (node) => {
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return text === 'chat' || text === 'chat with us' || text === 'message us';
  };

  const collectNativeLaunchers = () => {
    const candidates = [];

    NATIVE_LAUNCHER_SELECTORS.forEach((selector) => {
      try {
        document.querySelectorAll(selector).forEach((node) => candidates.push(node));
      } catch (error) {
        // Ignore unsupported selectors in older browsers.
      }
    });

    document.querySelectorAll('button, [role="button"]').forEach((node) => {
      if (hasChatText(node)) candidates.push(node);
    });

    return [...new Set(candidates)].filter((node) => !isRejected(node) && isVisible(node));
  };

  const findShopifyInboxLauncher = () => {
    const candidates = collectNativeLaunchers();
    const nonIframe = candidates.find((node) => node.tagName !== 'IFRAME');
    const launcher = nonIframe || candidates[0] || null;
    state.lastLauncher = launcher;
    return launcher;
  };

  const hasPublicOpenApi = () => Boolean(
    (window.ShopifyInbox && typeof window.ShopifyInbox.open === 'function') ||
    (window.ShopifyChat && typeof window.ShopifyChat.open === 'function') ||
    (window.Shopify && window.Shopify.Inbox && typeof window.Shopify.Inbox.open === 'function') ||
    (window.Shopify && window.Shopify.chat && typeof window.Shopify.chat.open === 'function')
  );

  const openWithPublicApi = () => {
    const openers = [
      window.ShopifyInbox && window.ShopifyInbox.open,
      window.ShopifyChat && window.ShopifyChat.open,
      window.Shopify && window.Shopify.Inbox && window.Shopify.Inbox.open,
      window.Shopify && window.Shopify.chat && window.Shopify.chat.open
    ];

    for (const opener of openers) {
      if (typeof opener !== 'function') continue;
      try {
        opener.call(window);
        return true;
      } catch (error) {
        // Try the next known Shopify Inbox adapter.
      }
    }

    return false;
  };

  const isChatWindowOpen = () => {
    const expanded = document.querySelector('[aria-expanded="true"][aria-label*="chat" i], [data-shopify-inbox][aria-expanded="true"], [data-shopify-chat][aria-expanded="true"]');
    if (expanded) return true;

    const largeChatFrame = Array.from(document.querySelectorAll('iframe')).find((frame) => {
      const label = `${frame.title || ''} ${frame.id || ''} ${frame.className || ''} ${frame.src || ''}`.toLowerCase();
      if (!label.includes('chat') && !label.includes('inbox')) return false;
      const rect = frame.getBoundingClientRect();
      return rect.width >= 260 && rect.height >= 320;
    });

    return Boolean(largeChatFrame);
  };

  const waitForShopifyInboxLauncher = (timeout = 5000) => new Promise((resolve) => {
    const immediate = findShopifyInboxLauncher();
    if (immediate || hasPublicOpenApi()) {
      resolve(immediate);
      return;
    }

    if (!document.body) {
      document.addEventListener('DOMContentLoaded', () => resolve(findShopifyInboxLauncher()), { once: true });
      window.setTimeout(() => resolve(null), timeout);
      return;
    }

    let settled = false;
    let observer;
    let interval;
    const started = Date.now();

    const finish = (node) => {
      if (settled) return;
      settled = true;
      if (observer) observer.disconnect();
      if (interval) window.clearInterval(interval);
      resolve(node || null);
    };

    const check = () => {
      const found = findShopifyInboxLauncher();
      if (found || hasPublicOpenApi()) {
        finish(found);
        return;
      }
      if (Date.now() - started >= timeout) finish(null);
    };

    observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'src', 'title', 'aria-label'] });
    interval = window.setInterval(check, 250);
    window.setTimeout(() => finish(null), timeout + 80);
  });

  const clickNativeLauncher = async (launcher) => {
    if (!launcher || typeof launcher.click !== 'function') return false;

    try {
      launcher.click();
      await new Promise((resolve) => window.setTimeout(resolve, 650));
      return true;
    } catch (error) {
      return false;
    }
  };

  const installFooterContactLink = () => {
    if (!root) return;
    const footerContact = document.querySelector('.tl-final-footer__contact');
    const template = root.querySelector('[data-treedix-live-chat-footer-template]');
    if (!footerContact || !template) return;
    if (footerContact.querySelector('[data-treedix-live-chat-trigger]')) return;

    footerContact.appendChild(template.content.cloneNode(true));
  };

  const open = async () => {
    if (state.opening) return false;
    state.opening = true;

    try {
      if (openWithPublicApi()) {
        state.opening = false;
        return true;
      }

      const launcher = await waitForShopifyInboxLauncher(5000);
      if (!launcher) {
        state.opening = false;
        return false;
      }

      const clicked = await clickNativeLauncher(launcher);
      state.opening = false;
      return clicked || isChatWindowOpen();
    } catch (error) {
      console.error('[TREEDIX Live Chat]', error);
      state.opening = false;
      return false;
    }
  };

  const bindTriggers = () => {
    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-treedix-live-chat-trigger]');
      if (!trigger) return;

      event.preventDefault();
      const opened = await window.TREEDIXLiveChat.open();

      if (!opened) {
        window.location.href = trigger.dataset.fallbackUrl || trigger.href || fallbackUrl;
      }
    });
  };

  window.TREEDIXLiveChat = {
    open,
    findShopifyInboxLauncher,
    waitForShopifyInboxLauncher,
    isAvailable: () => Boolean(findShopifyInboxLauncher() || hasPublicOpenApi()),
    _state: state
  };

  installFooterContactLink();
  bindTriggers();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installFooterContactLink, { once: true });
  }
})();
